import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { FaSearch, FaMapMarkerAlt, FaShieldAlt, FaChevronLeft, FaChevronRight, FaRupeeSign } from 'react-icons/fa'

const slides = [
  {
    id: 1,
    image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&q=80&w=2000',
    tag: 'Curated for Working Women',
    titleLine1: 'A Home That',
    titleHighlight: 'Understands You',
    description: 'Experience the perfect blend of luxury, safety, and community. Discover premium PG accommodations verified for your peace of mind.'
  },
  {
    id: 2,
    image: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&q=80&w=2000',
    tag: 'Unmatched Security',
    titleLine1: 'Peace of Mind',
    titleHighlight: 'Guaranteed',
    description: 'With 24x7 CCTV surveillance and dedicated female wardens, your safety is our top priority.'
  },
  {
    id: 3,
    image: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&q=80&w=2000',
    tag: 'Premium Amenities',
    titleLine1: 'Experience True',
    titleHighlight: 'Comfort & Luxury',
    description: 'Fully furnished rooms, high-speed WiFi, daily housekeeping.'
  }
]

const Hero = () => {
  const [current, setCurrent] = useState(0)
  const [isPaused, setIsPaused] = useState(false)

  useEffect(() => {
    const timer = setInterval(() => {
      if (!isPaused) {
        setCurrent((prev) => (prev === slides.length - 1 ? 0 : prev + 1))
      }
    }, 5000)
    return () => clearInterval(timer)
  }, [isPaused])

  const nextSlide = () => setCurrent(current === slides.length - 1 ? 0 : current + 1)
  const prevSlide = () => setCurrent(current === 0 ? slides.length - 1 : current - 1)

  return (
    <section
      className="relative min-h-[80vh] md:min-h-[90vh] flex items-center justify-center overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >

      {/* Background Images Slider */}
      <AnimatePresence initial={false}>
        <motion.div
          key={current}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2, ease: "easeInOut" }}
          className="absolute inset-0 z-0"
        >
          <img
            src={slides[current].image}
            alt="Premium Living"
            className="w-full h-full object-cover"
          />
          {/* Dark gradient overlay for better text contrast */}
          <div className="absolute inset-0 bg-gradient-to-r from-secondary-900 via-secondary-900/80 to-secondary-900/40"></div>
        </motion.div>
      </AnimatePresence>

      <div className="container-custom relative z-10 w-full px-4 pt-16 pb-40 md:py-20">
        <div className="max-w-2xl lg:max-w-3xl text-left">
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.6 }}
              className="text-left"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-5 md:py-2.5 rounded-full bg-primary-900/60 backdrop-blur-md border border-primary-500/30 text-primary-200 text-xs md:text-sm font-bold tracking-widest uppercase mb-3 md:mb-8 shadow-lg">
                <span className="relative flex h-2 w-2 md:h-2.5 md:w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 md:h-2.5 md:w-2.5 bg-primary-500"></span>
                </span>
                {slides[current].tag}
              </div>

              <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-7xl font-heading font-extrabold text-white leading-tight mb-3 md:mb-6">
                Pune's Best <br className="hidden sm:block" />
                <span className="text-primary-400 drop-shadow-md">
                  Premium Girls PG
                </span>
              </h1>
              <p className="text-xs sm:text-sm md:text-xl text-gray-300 mb-4 md:mb-10 leading-relaxed max-w-xl md:max-w-2xl font-light">
                {slides[current].description}
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/properties" className="btn-primary px-6 py-2.5 md:px-10 md:py-4 text-sm md:text-lg flex items-center justify-center gap-3 group">
                  <FaSearch className="group-hover:rotate-12 transition-transform" /> Find Your Stay
                </Link>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Slider Controls */}
      <div className="absolute top-1/2 -translate-y-1/2 left-2 md:left-8 z-20 hidden md:flex">
        <button
          onClick={prevSlide}
          className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white flex items-center justify-center hover:bg-primary-600 transition-colors"
        >
          <FaChevronLeft />
        </button>
      </div>
      <div className="absolute top-1/2 -translate-y-1/2 right-2 md:right-8 z-20 hidden md:flex">
        <button
          onClick={nextSlide}
          className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white flex items-center justify-center hover:bg-primary-600 transition-colors"
        >
          <FaChevronRight />
        </button>
      </div>

      {/* Slider Dots */}
      <div className="absolute bottom-40 md:bottom-36 left-1/2 -translate-x-1/2 z-20 flex gap-2 md:gap-3">
        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrent(idx)}
            className={`w-2 h-2 md:w-3 md:h-3 rounded-full transition-all duration-300 ${idx === current ? 'bg-primary-500 scale-125' : 'bg-white/30 hover:bg-white/50'}`}
          />
        ))}
      </div>

      {/* Floating Info Bar at the bottom */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        className="absolute bottom-8 left-0 w-full z-20"
      >
        <div className="container-custom px-4">
          <div className="glass-card bg-white/95 backdrop-blur-xl p-3 md:p-4 flex flex-col md:flex-row justify-between items-center divide-y md:divide-y-0 md:divide-x divide-gray-200/60 shadow-2xl mx-auto max-w-2xl lg:max-w-4xl rounded-2xl border border-white/40">
            <div className="flex items-center gap-2 md:gap-4 px-3 md:px-8 py-2 md:py-0 w-full md:w-auto">
              <div className="bg-primary-50 p-2 md:p-4 rounded-2xl text-primary-600 shadow-inner shrink-0">
                <FaShieldAlt className="text-lg md:text-2xl" />
              </div>
              <div>
                <div className="text-[9px] md:text-xs text-gray-500 font-bold uppercase tracking-wider mb-0.5">100% Verified</div>
                <div className="font-heading font-extrabold text-secondary-900 text-xs md:text-lg">Premium Stays</div>
              </div>
            </div>
            <div className="flex items-center gap-2 md:gap-4 px-3 md:px-8 py-2 md:py-0 w-full md:w-auto">
              <div className="bg-accent-50 p-2 md:p-4 rounded-2xl text-accent-600 shadow-inner shrink-0">
                <FaRupeeSign className="text-lg md:text-2xl" />
              </div>
              <div>
                <div className="text-[9px] md:text-xs text-gray-500 font-bold uppercase tracking-wider mb-0.5">Zero Brokerage</div>
                <div className="font-heading font-extrabold text-secondary-900 text-xs md:text-lg">No Hidden Costs</div>
              </div>
            </div>
            <div className="flex items-center gap-2 md:gap-4 px-3 md:px-8 py-2 md:py-0 w-full md:w-auto">
              <div className="bg-secondary-50 p-2 md:p-4 rounded-2xl text-secondary-600 shadow-inner shrink-0">
                <FaMapMarkerAlt className="text-lg md:text-2xl" />
              </div>
              <div>
                <div className="text-[9px] md:text-xs text-gray-500 font-bold uppercase tracking-wider mb-0.5">Prime Locations</div>
                <div className="font-heading font-extrabold text-secondary-900 text-xs md:text-lg">Near IT Hubs</div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  )
}

export default Hero